import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const channels = [
  { label: "电视剧", path: "/category/电视剧" },
  { label: "综艺", path: "/category/综艺" },
  { label: "电影", path: "/category/电影" },
  { label: "动漫", path: "/category/动漫" },
  { label: "纪录片", path: "/category/纪录片" },
];

const linkBase =
  "px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer";

export default function Navbar() {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const submitSearch = () => {
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setSearchOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center gap-2 w-full px-4 md:px-8">
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0 cursor-pointer"
          aria-label="芒果tv 首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-mickey-fill text-lg" />
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-primary-600">
            芒果tv
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${linkBase} ${
                isActive
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`
            }
          >
            首页
          </NavLink>
          {channels.map((c) => (
            <NavLink
              key={c.path}
              to={c.path}
              className={({ isActive }) =>
                `${linkBase} ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                }`
              }
            >
              {c.label}
            </NavLink>
          ))}
          <NavLink
            to="/news"
            className={({ isActive }) =>
              `${linkBase} ${
                isActive
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`
            }
          >
            资讯
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden lg:inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
            <i className="ri-fire-line" />
            秋季片单上线
          </span>

          <div className="hidden md:flex items-center">
            {searchOpen ? (
              <div className="flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1">
                <i className="ri-search-line text-foreground-500 text-sm" />
                <input
                  ref={searchRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                  placeholder="搜索影视、综艺、资讯"
                  className="w-48 bg-transparent px-2 py-1.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={submitSearch}
                  className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="展开搜索"
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-search-line text-lg" />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer md:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-3">
          <div className="mb-3 flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1">
            <i className="ri-search-line text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder="搜索影视、综艺、资讯"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <NavLink
              to="/"
              end
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700"
                }`
              }
            >
              首页
            </NavLink>
            {channels.map((c) => (
              <NavLink
                key={c.path}
                to={c.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
            <NavLink
              to="/news"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700"
                }`
              }
            >
              资讯
            </NavLink>
            <NavLink
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-700"
            >
              关于我们
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
}